/** Shared dining layout dimensions (metres). Used by 2D SVG generation and 3D primitives. */
export const DINING_TABLE_WIDTH_M = 0.9;
export const DINING_CHAIR_WIDTH_M = 0.45;
export const DINING_CHAIR_DEPTH_M = 0.5;
export const DINING_ARMCHAIR_SIZE_M = 0.55;
export const DINING_CHAIR_TABLE_GAP_M = 0.042;

/** Back stub width as a fraction of chair depth (from standalone `stol ny-01.svg`). */
export const DINING_CHAIR_BACK_DEPTH_RATIO = 7.08 / 47.22;

export const DINING_ROUND_CHAIR_COUNT = 6;

/** Round 6-seater table diameter (metres). */
export const DINING_ROUND_TABLE_DIAMETER_M = 1.5;

/**
 * Rectangular table layouts (metres).
 * Width is always 900 mm (between side chairs). Length varies per seat count.
 */
export const DINING_RECT_TABLE_LAYOUTS = {
  "2-chair": {
    tableWidth: DINING_TABLE_WIDTH_M,
    tableDepth: 1.2,
    chairRows: 1,
    chairRowSpread: 1,
  },
  "4-chair": {
    tableWidth: DINING_TABLE_WIDTH_M,
    tableDepth: 1.4,
    chairRows: 2,
    /** Compress row spacing toward table centre; side gap to table unchanged. */
    chairRowSpread: 0.55,
  },
  "6-chair": {
    tableWidth: DINING_TABLE_WIDTH_M,
    tableDepth: 2.1,
    chairRows: 3,
    chairRowSpread: 0.72,
  },
} as const;

export type DiningRectLayoutKey = keyof typeof DINING_RECT_TABLE_LAYOUTS;

/** The plain table reuses the tabletop dimensions of the four-chair table. */
export const DINING_PLAIN_TABLE_LAYOUT = DINING_RECT_TABLE_LAYOUTS["4-chair"];

export const DINING_RECT_SYMBOL_FILES: Record<
  DiningRectLayoutKey,
  string
> = {
  "2-chair": "2-bord ny-01.svg",
  "4-chair": "4-bord ny-01.svg",
  "6-chair": "6 bord ny-01.svg",
};

export const DINING_ROUND_SYMBOL_FILE = "runt bord ny-01.svg";
export const DINING_PLAIN_TABLE_SYMBOL_FILE = "plain table.svg";
export const DINING_PLAIN_ROUND_TABLE_SYMBOL_FILE = "plain round table.svg";

/** Total symbol width for a rectangular dining group (table + gap + chairs). */
export function diningRectSymbolLengthM(tableWidthM: number): number {
  return (
    tableWidthM + 2 * DINING_CHAIR_TABLE_GAP_M + 2 * DINING_CHAIR_DEPTH_M
  );
}

export function diningRectChairXOffsetM(tableWidthM: number): number {
  return (
    tableWidthM / 2 + DINING_CHAIR_TABLE_GAP_M + DINING_CHAIR_DEPTH_M / 2
  );
}

export function diningRoundTableRadiusM(): number {
  return DINING_ROUND_TABLE_DIAMETER_M / 2;
}

export function diningRoundChairOrbitRadiusM(): number {
  return (
    diningRoundTableRadiusM() +
    DINING_CHAIR_TABLE_GAP_M +
    DINING_CHAIR_DEPTH_M / 2
  );
}
